// Canonical member-plan fixture builders (prompt-context integration
// contract): the plan rollover write path, the composer's weeklyPlan prompt
// reader, and the voice-session weekly-plan block all build stored-row shapes
// through this ONE set of builders, so a change to the stored theme shape
// breaks every consumer's tests in the same commit instead of drifting one
// hand-rolled literal at a time.
//
// Plan-item rows are NOT built here: the top-level package barrel must never
// transitively reach `src/db/` (the barrel-shape scanner keeps the RDS Data
// API client out of the mobile bundle), so the theme-flavoured delegate to
// the canonical `$inferSelect`-derived builder lives beside it in
// `db/plan-item-fixtures` — import `buildThemePlanItemRow` from
// `@galapagos/shared/db`. The member-plan row is a hand-written mirror for the
// same reason, and the drift-guard test beside this file holds it in step with
// the table via the journey fixtures' assignability + key-set assertions.

import type { z } from 'zod';
import type { aiThemeConfigSchema } from './schema';
import { intakeFocusConfigSchema } from './intake-capabilities';

export type AiThemeConfigFixture = z.infer<typeof aiThemeConfigSchema>;

/** A `member_plans` row as a Drizzle select returns it. */
export interface MemberPlanRowFixture {
  id: string;
  memberId: string;
  kind: 'foundation' | 'weekly';
  status: 'active' | 'completed' | 'ended_early';
  startsOn: string;
  endsOn: string | null;
  timezone: string;
  firstHomeArrivedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

const FIXTURE_INSTANT = new Date('2026-08-30T09:00:00.000Z');

export function buildAiThemeConfig(
  overrides: Partial<AiThemeConfigFixture> = {},
): AiThemeConfigFixture {
  return {
    suggestionId: 'sugg-0001',
    rank: 1,
    category: 'Cravings',
    title: 'Managing evening cravings',
    blurb: 'Notice the moments cravings spike after dinner and practise one small pause each time.',
    ...overrides,
  };
}

export function buildMemberPlanRow(
  overrides: Partial<MemberPlanRowFixture> = {},
): MemberPlanRowFixture {
  return {
    id: 'plan-0001',
    memberId: 'member-0001',
    kind: 'weekly',
    status: 'active',
    startsOn: '2026-08-30',
    endsOn: '2026-09-06',
    timezone: 'Europe/London',
    firstHomeArrivedAt: null,
    createdAt: FIXTURE_INSTANT,
    updatedAt: FIXTURE_INSTANT,
    ...overrides,
  };
}

/** Stored intake theme shared by its writer and prompt-history readers. */
export function buildIntakeFocusConfig(
  overrides: Partial<z.infer<typeof intakeFocusConfigSchema>> = {},
) {
  return intakeFocusConfigSchema.parse({
    title: 'Evenings',
    description: 'Pause after work.',
    ...overrides,
  });
}
